import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CommandBar, ICommandBarItemProps, Spinner, SpinnerSize, TextField} from '@fluentui/react';
import {IStorefrontBanner, IStorefrontSection} from "../../models/Storefront";
import {
    useDeleteStorefrontBannerMutation,
    useGetStorefrontBannersQuery,
    useSaveStorefrontBannerMutation,
    useSaveStorefrontBannersMutation,
} from "../../store/endpoints/StorefrontBannerEndpoints";
import {debounce} from "lodash";
import EditableText from "../Inputs/EditableText";
import CompactColorPicker from "../Inputs/CompactColorPicker";
import ToggleField from "../Common/ToggleField";
import ImageEditor from "../Inputs/ImageEditor";

interface IStorefrontBannerEditorProps {
    storefront?: string;
    isGlobal?: boolean;
}

const StorefrontBannerEditor: React.FC<IStorefrontBannerEditorProps> = (props) => {
    const [storefront, setStorefront] = useState<string>();
    const [effectiveBanners, setEffectiveBanners ] = useState<IStorefrontBanner[]>([]);
    useEffect(() => {
        setStorefront(props.isGlobal ? 'global' : props.storefront);
    },[props.isGlobal, props.storefront]);
    const [saving, setSaving] = useState(false);
    const banners = useGetStorefrontBannersQuery(storefront ?? '', {skip: !storefront});
    const [setBanner] = useSaveStorefrontBannerMutation();
    const [setBanners] = useSaveStorefrontBannersMutation();
    const [deleteBanner] = useDeleteStorefrontBannerMutation();
    const [needsScroll, setNeedsScroll] = useState<boolean>(false);
    useEffect(() => {
        if (banners.data?.items) {
            setEffectiveBanners([...banners.data.items]);
        }
    }, [banners.data?.items]);

    useEffect(() => {
        if(effectiveBanners && needsScroll) {
            scrollToBottom()
            setNeedsScroll(false);
        }
    }, [effectiveBanners]);

    const [dragOver, setDragOver] = React.useState(false);
    const handleDragOverStart = () => setDragOver(true);
    const handleDragOverEnd = () => setDragOver(false);
    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        event.dataTransfer.setData('text', event.currentTarget.id);
    }
    const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }
    const handleDrop = (event: React.DragEvent<HTMLDivElement>, currentSection: number) => {
        const id = event.dataTransfer.getData('text')?.replace('banners_', '');
        const sourceIndex = parseInt(id ?? '-1');
        if (banners.data?.items && sourceIndex !== -1 && currentSection !== -1) {
            const initialBanners = [...banners.data.items];
            const [removed] = initialBanners.splice(sourceIndex, 1); // Remove the dragged column
            initialBanners.splice(currentSection, 0, removed); // Insert it at the current column's indexorder

            if(storefront && initialBanners){
                const updatedBanners = initialBanners.map((banner, index) => ({
                    ...banner,
                    sortOrder: index + 1
                }));
                setBanners({storefront: storefront ?? "", banners: updatedBanners});
            }
        }
        setDragOver(false);
    }

    const debouncedUpdateBanners = useCallback(
        debounce((val:IStorefrontSection[] | undefined) => {
            if(val && storefront) {
                setBanners({banners: val, storefront: storefront});
            }
        }, 2000, {leading: false}),
        [storefront]
    );
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };


    const commands: ICommandBarItemProps[] = [
        {
            key: 'add',
            text: 'Add Banner',
            title: 'Add Banner',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {
                const commandSections = [...(banners.data?.items ?? [])];
                const newBanner = { name: `Banner ${(banners.data?.items.length ?? 0) + 1}`, imageUrl: '', linkUrl: '', altText: '', sortOrder: commandSections.length + 1, storefront: storefront, disabled: true, shared: props.isGlobal} as IStorefrontBanner;
                if(storefront) {
                    setBanner({banner: newBanner, storefront: storefront}).unwrap().then(() => {
                        setNeedsScroll(true);
                    });
                }
            }
        }
    ]

    const getSectionCommands = (idx: number): ICommandBarItemProps[] => {
        return [

            {
                key: 'enableDisable',
                text: 'Published',
                title: 'Publish Banner',
                disabled: saving,
                onRender: () => <ToggleField inline={true} checked={effectiveBanners?.[idx].disabled != true} label={'Published'} onChange={(e, v) => {
                    if (effectiveBanners && storefront) {
                        const b = [...effectiveBanners];
                        b[idx] = {...b[idx], disabled: v === false};
                        setSaving(true);
                        setBanners({banners: b, storefront: storefront}).unwrap().then(() => {setSaving(false)});
                    }
                }} />,
            },
            {
                key: 'moveUp',
                text: 'Move Up',
                title: 'Move Up',
                iconProps: {iconName: 'ChevronUp'},
                disabled: idx === 0 || saving,
                onClick: () => {
                    if (effectiveBanners && storefront) {
                        const b = [...effectiveBanners];
                        const [movedBanner] = b.splice(idx, 1);
                        b.splice(idx - 1, 0, movedBanner);
                        setSaving(true);
                        setBanners({banners: b, storefront: storefront}).unwrap().then(() => {setSaving(false)});
                    }
                }
            },
            {
                key: 'moveDown',
                text: 'Move Down',
                title: 'Move Down',
                iconProps: {iconName: 'ChevronDown'},
                disabled: (effectiveBanners ? idx === effectiveBanners.length - 1 : true) || saving,
                onClick: () => {
                    if (effectiveBanners && storefront) {
                        const b = [...effectiveBanners];
                        const [movedSection] = b.splice(idx, 1);
                        b.splice(idx + 1, 0, movedSection);
                        setSaving(true);
                        setBanners({banners: b, storefront: storefront}).unwrap().then(() => {setSaving(false)});
                    }
                }
            },
            {
                key: 'delete',
                text: 'Delete',
                title: 'Delete Banner',
                disabled: saving,
                iconProps: {iconName: 'Delete'},
                onClick: () => {
                    const bannerToDelete = effectiveBanners?.[idx];
                    if (bannerToDelete && bannerToDelete.id && storefront) {
                        setSaving(true);
                        deleteBanner({storefront: storefront, id: bannerToDelete.id}).unwrap().then(() => {
                            setSaving(false);
                        })
                    }
                }
            }
        ]
    }

    const updateBanner = (index: number, banner: IStorefrontBanner) => {
        const banners = [...(effectiveBanners ?? [])];
        banners[index] = banner

        setEffectiveBanners(() => [...banners])
        debouncedUpdateBanners(banners)
    }
    return (
        <div style={{width: '90%', minWidth: '300px'}}>

            {(saving || banners.isLoading || banners.isFetching) ?
                <div style={{alignSelf: 'self-start'}}><Spinner label="Loading..." size={SpinnerSize.small} ariaLive="assertive"/></div> : <CommandBar items={commands} farItems={[]}/>
            }
            {effectiveBanners.map((banner, idx) => (
                <div id={`banners-${idx}`} key={idx} draggable
                     onDragOver={enableDropping}
                     onDrop={(ev) => handleDrop(ev, idx)}
                     onDragEnter={handleDragOverStart}
                     onDragLeave={handleDragOverEnd}
                     style={dragOver ? {width: '100%', height: '100%', background: 'rgb(207, 220, 181)'} : {}}
                >
                    <div id={`banners_${idx}`} className={'storefront-section'} draggable onDragStart={handleDragStart}>
                        <div style={{paddingBottom: '10px'}}>
                            <EditableText
                                onChange={v => updateBanner(idx, {...banner, name: v})}
                                value={banner.name || `Banner ${idx + 1}`}
                            />
                        </div>
                        <CommandBar items={getSectionCommands(idx)} farItems={[]}/>
                        <ImageEditor
                            value={banner.imageUrl}
                            onChange={v => updateBanner(idx, {...banner, imageUrl: v })}
                            imageId={`banner-${idx}`}
                            onClear={() => updateBanner(idx, {...banner, imageUrl: '' })}
                            imageType='StorefrontBanner'
                        />
                        <TextField
                            label='Banner Link'
                            value={banner.linkUrl}
                            onChange={(_, v) => updateBanner(idx, {...banner, linkUrl: v ?? '' })}
                        />
                        <TextField
                            label='Banner Alt Text'
                            value={banner.altText}
                            onChange={(_, v) => updateBanner(idx, { ...banner, altText: v ?? '' })}
                        />
                        <CompactColorPicker label={'Background Color'} color={banner.backgroundColor ?? '#f0f0f0'} onChange={( c) => updateBanner(idx, { ...banner, backgroundColor: c })} />
                    </div>
                </div>
            ))}
            {(saving || banners.isLoading || banners.isFetching || !banners.data?.items || banners.data.items.length === 0) ?
                <div></div> : <CommandBar items={commands} farItems={[]}/>
            }
            <div ref={bottomRef} />
        </div>
    );
};

export default StorefrontBannerEditor;
